<script>
    import { onMount } from 'svelte';
    import { aiProvidersAPI } from './api.js';

    let providers = [];
    let showModal = false;
    let editingProvider = null;
    let loading = true;
    let loadingModels = false;

    // Fallback static list (used if API fails)
    const fallbackProviderTypes = {
        anthropic: { label: 'Anthropic (Claude)', icon: '🟣', models: [] },
        openai: { label: 'OpenAI (ChatGPT)', icon: '🟢', models: [] },
        openrouter: { label: 'OpenRouter', icon: '🌐', description: 'Acceso a múltiples modelos', models: [] },
        grok: { label: 'xAI (Grok)', icon: '⚡', models: [] },
        gemini: { label: 'Google (Gemini)', icon: '🔵', models: [] },
        cohere: { label: 'Cohere', icon: '🟠', models: [] },
        llama: { label: 'Llama Local (Ollama)', icon: '🦙', models: [] },
        custom: { label: 'Custom Endpoint', icon: '🔧', models: [] }
    };

    // Models catalog from backend
    let modelsCatalog = {};

    let formData = {
        name: '',
        provider_type: 'anthropic',
        api_key: '',
        api_endpoint: '',
        model_name: '',
        is_active: true
    };

    // Use backend catalog if loaded, otherwise fallback
    $: effectiveCatalog = Object.keys(modelsCatalog).length > 0 ? modelsCatalog : fallbackProviderTypes;

    // Computed: current provider info
    $: currentProvider = effectiveCatalog[formData.provider_type] || { models: [], label: formData.provider_type };
    $: availableModels = currentProvider.models || [];
    $: selectedModel = availableModels.find(m => m.id === formData.model_name);

    onMount(async () => {
        await Promise.all([loadProviders(), loadModelsCatalog()]);
    });

    async function loadModelsCatalog() {
        try {
            loadingModels = true;
            modelsCatalog = await aiProvidersAPI.getModels();
        } catch (error) {
            console.error('Error loading models catalog:', error);
            // Keep using fallback
        } finally {
            loadingModels = false;
        }
    }

    async function loadProviders() {
        try {
            loading = true;
            providers = await aiProvidersAPI.getAll();
        } catch (error) {
            console.error('Error loading providers:', error);
            alert('Error loading providers');
        } finally {
            loading = false;
        }
    }

    function openModal(provider = null) {
        editingProvider = provider;
        if (provider) {
            formData = {
                name: provider.name,
                provider_type: provider.provider_type,
                api_key: provider.api_key || '',
                api_endpoint: provider.api_endpoint || '',
                model_name: provider.model_name || '',
                is_active: provider.is_active
            };
        } else {
            formData = {
                name: '',
                provider_type: 'anthropic',
                api_key: '',
                api_endpoint: '',
                model_name: '',
                is_active: true
            };
        }
        showModal = true;
    }

    function closeModal() {
        showModal = false;
        editingProvider = null;
    }

    async function handleSubmit() {
        try {
            if (editingProvider) {
                await aiProvidersAPI.update(editingProvider.id, formData);
            } else {
                await aiProvidersAPI.create(formData);
            }
            closeModal();
            await loadProviders();
        } catch (error) {
            console.error('Error saving provider:', error);
            alert('Error saving provider: ' + error.message);
        }
    }

    async function handleDelete(id) {
        if (!confirm('¿Estás seguro de eliminar este proveedor?')) return;

        try {
            await aiProvidersAPI.delete(id);
            await loadProviders();
        } catch (error) {
            console.error('Error deleting provider:', error);
            alert('Error deleting provider');
        }
    }

    async function toggleActive(provider) {
        try {
            await aiProvidersAPI.update(provider.id, { is_active: !provider.is_active });
            await loadProviders();
        } catch (error) {
            console.error('Error toggling provider:', error);
            alert('Error updating provider');
        }
    }

    function getProviderIcon(type) {
        const catalog = Object.keys(modelsCatalog).length > 0 ? modelsCatalog : fallbackProviderTypes;
        return catalog[type]?.icon || '🤖';
    }

    function getProviderLabel(type) {
        const catalog = Object.keys(modelsCatalog).length > 0 ? modelsCatalog : fallbackProviderTypes;
        return catalog[type]?.label || type;
    }

    function formatPrice(price) {
        if (price === 0) return 'Gratis';
        if (price < 0.01) return `$${price.toFixed(4)}`;
        if (price < 1) return `$${price.toFixed(3)}`;
        return `$${price.toFixed(2)}`;
    }

    function getModelInfo(providerType, modelId) {
        const catalog = Object.keys(modelsCatalog).length > 0 ? modelsCatalog : fallbackProviderTypes;
        const provider = catalog[providerType];
        if (!provider) return null;
        return provider.models?.find(m => m.id === modelId);
    }

    function handleProviderChange() {
        // Reset model when provider changes
        formData.model_name = '';
    }
</script>

<div class="ai-providers-container">
    <div class="header">
        <div>
            <h2>Proveedores de IA</h2>
            <p class="subtitle">Configura los proveedores de IA para tus chatbots</p>
        </div>
        <button class="btn-primary" on:click={() => openModal()}>
            + Agregar Proveedor
        </button>
    </div>

    {#if loading || loadingModels}
        <div class="loading">Cargando proveedores...</div>
    {:else if providers.length === 0}
        <div class="empty-state">
            <p>No hay proveedores configurados</p>
            <button class="btn-primary" on:click={() => openModal()}>
                Agregar tu primer proveedor
            </button>
        </div>
    {:else}
        <div class="providers-grid">
            {#each providers as provider (provider.id)}
                {@const modelInfo = getModelInfo(provider.provider_type, provider.model_name)}
                <div class="provider-card {provider.is_active ? 'active' : 'inactive'}">
                    <div class="provider-header">
                        <div class="provider-icon">
                            {getProviderIcon(provider.provider_type)}
                        </div>
                        <div class="provider-info">
                            <h3>{provider.name}</h3>
                            <span class="provider-type">{getProviderLabel(provider.provider_type)}</span>
                        </div>
                        <div class="provider-status">
                            <button
                                class="status-toggle {provider.is_active ? 'active' : ''}"
                                on:click={() => toggleActive(provider)}
                                title={provider.is_active ? 'Desactivar' : 'Activar'}
                            >
                                {provider.is_active ? '✓ Activo' : '○ Inactivo'}
                            </button>
                        </div>
                    </div>

                    <div class="provider-details">
                        <div class="detail-item">
                            <span class="label">Modelo:</span>
                            <span class="value">{modelInfo?.name || provider.model_name || 'No especificado'}</span>
                        </div>
                        {#if modelInfo}
                            <div class="detail-item pricing">
                                <span class="label">Costo /1M tokens:</span>
                                <span class="value price-info">
                                    {#if modelInfo.free}
                                        <span class="free-badge">🎉 Gratis</span>
                                    {:else if modelInfo.local}
                                        <span class="free-badge">Local - Gratis</span>
                                    {:else}
                                        <span class="price-item">↓ {formatPrice(modelInfo.input)}</span>
                                        <span class="price-item">↑ {formatPrice(modelInfo.output)}</span>
                                    {/if}
                                </span>
                            </div>
                            {#if modelInfo.context}
                                <div class="detail-item">
                                    <span class="label">Contexto:</span>
                                    <span class="value">{modelInfo.context} tokens</span>
                                </div>
                            {/if}
                        {/if}
                        {#if provider.api_endpoint}
                            <div class="detail-item">
                                <span class="label">Endpoint:</span>
                                <span class="value endpoint">{provider.api_endpoint}</span>
                            </div>
                        {/if}
                        <div class="detail-item">
                            <span class="label">API Key:</span>
                            <span class="value">{provider.api_key ? '••••••••' : 'No configurada'}</span>
                        </div>
                    </div>

                    <div class="provider-actions">
                        <button class="btn-edit" on:click={() => openModal(provider)}>
                            Editar
                        </button>
                        <button class="btn-delete" on:click={() => handleDelete(provider.id)}>
                            Eliminar
                        </button>
                    </div>
                </div>
            {/each}
        </div>
    {/if}
</div>

{#if showModal}
    <div class="modal-overlay" on:click={closeModal}>
        <div class="modal" on:click|stopPropagation>
            <div class="modal-header">
                <h3>{editingProvider ? 'Editar Proveedor' : 'Nuevo Proveedor'}</h3>
                <button class="close-button" on:click={closeModal}>×</button>
            </div>

            <form on:submit|preventDefault={handleSubmit} class="modal-form">
                <div class="form-group">
                    <label for="name">Nombre *</label>
                    <input
                        type="text"
                        id="name"
                        bind:value={formData.name}
                        placeholder="ej: Claude Principal"
                        required
                    />
                </div>

                <div class="form-group">
                    <label for="provider_type">Tipo de Proveedor *</label>
                    <select id="provider_type" bind:value={formData.provider_type} on:change={handleProviderChange} required>
                        {#each Object.entries(effectiveCatalog) as [key, provider]}
                            <option value={key}>{provider.icon} {provider.label}</option>
                        {/each}
                    </select>
                    {#if currentProvider.description}
                        <small class="provider-desc">{currentProvider.description}</small>
                    {/if}
                </div>

                <div class="form-group">
                    <label for="model_name">Modelo *</label>
                    {#if availableModels.length > 0}
                        <select id="model_name" bind:value={formData.model_name} required>
                            <option value="">-- Selecciona un modelo --</option>
                            {#each availableModels as model}
                                <option value={model.id}>
                                    {model.name}
                                    {#if model.recommended}⭐{/if}
                                    {#if model.free || model.local}
                                        - GRATIS
                                    {:else}
                                        - ${model.input}/${model.output} por 1M
                                    {/if}
                                </option>
                            {/each}
                        </select>

                        {#if selectedModel}
                            <div class="model-details">
                                <div class="model-pricing">
                                    {#if selectedModel.free}
                                        <span class="free-badge">🎉 Modelo Gratuito - Sin costo</span>
                                    {:else if selectedModel.local}
                                        <span class="free-badge">🦙 Modelo Local - Sin costo</span>
                                    {:else}
                                        <div class="price-row">
                                            <span class="price-label">Input:</span>
                                            <span class="price-value">{formatPrice(selectedModel.input)} /1M tokens</span>
                                        </div>
                                        <div class="price-row">
                                            <span class="price-label">Output:</span>
                                            <span class="price-value">{formatPrice(selectedModel.output)} /1M tokens</span>
                                        </div>
                                    {/if}
                                </div>
                                {#if selectedModel.context}
                                    <div class="context-info">
                                        📝 Ventana de contexto: {selectedModel.context} tokens
                                    </div>
                                {/if}
                            </div>
                        {/if}
                    {:else}
                        <input
                            type="text"
                            id="model_name"
                            bind:value={formData.model_name}
                            placeholder="Nombre del modelo personalizado"
                        />
                        <small>Ingresa el nombre del modelo manualmente</small>
                    {/if}
                </div>

                <div class="form-group">
                    <label for="api_key">API Key {formData.provider_type !== 'llama' ? '*' : ''}</label>
                    <input
                        type="password"
                        id="api_key"
                        bind:value={formData.api_key}
                        placeholder={formData.provider_type === 'openrouter' ? 'sk-or-...' : 'sk-...'}
                        required={formData.provider_type !== 'llama' && formData.provider_type !== 'custom'}
                    />
                    <small>
                        {#if formData.provider_type === 'openrouter'}
                            Obtén tu API key en <a href="https://openrouter.ai/keys" target="_blank">openrouter.ai/keys</a>
                        {:else if formData.provider_type === 'anthropic'}
                            Obtén tu API key en <a href="https://console.anthropic.com/" target="_blank">console.anthropic.com</a>
                        {:else if formData.provider_type === 'openai'}
                            Obtén tu API key en <a href="https://platform.openai.com/api-keys" target="_blank">platform.openai.com</a>
                        {:else if formData.provider_type === 'llama'}
                            No requiere API key para modelos locales
                        {:else}
                            API key del proveedor
                        {/if}
                    </small>
                </div>

                {#if formData.provider_type === 'llama' || formData.provider_type === 'custom'}
                    <div class="form-group">
                        <label for="api_endpoint">Endpoint API</label>
                        <input
                            type="url"
                            id="api_endpoint"
                            bind:value={formData.api_endpoint}
                            placeholder="http://localhost:11434"
                        />
                        <small>URL del servidor de IA (para Llama/Ollama u otros locales)</small>
                    </div>
                {/if}

                <div class="form-group checkbox">
                    <label>
                        <input
                            type="checkbox"
                            bind:checked={formData.is_active}
                        />
                        <span>Activar este proveedor</span>
                    </label>
                </div>

                <div class="modal-actions">
                    <button type="button" class="btn-cancel" on:click={closeModal}>
                        Cancelar
                    </button>
                    <button type="submit" class="btn-submit">
                        {editingProvider ? 'Actualizar' : 'Crear'}
                    </button>
                </div>
            </form>
        </div>
    </div>
{/if}

<style>
    .ai-providers-container {
        padding: 2rem;
        height: 100%;
        overflow-y: auto;
        background: #f5f7fb;
    }

    .header {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        margin-bottom: 2rem;
    }

    .header h2 {
        margin: 0 0 0.5rem 0;
        font-size: 1.75rem;
        color: #333;
    }

    .subtitle {
        margin: 0;
        color: #666;
        font-size: 0.9rem;
    }

    .btn-primary {
        padding: 0.75rem 1.5rem;
        background: linear-gradient(135deg, #128C7E 0%, #075E54 100%);
        color: white;
        border: none;
        border-radius: 8px;
        cursor: pointer;
        font-size: 0.9375rem;
        font-weight: 600;
        transition: transform 0.2s;
    }

    .btn-primary:hover {
        transform: translateY(-2px);
    }

    .loading {
        text-align: center;
        padding: 3rem;
        color: #666;
    }

    .empty-state {
        text-align: center;
        padding: 3rem;
        background: white;
        border-radius: 12px;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    }

    .empty-state p {
        color: #999;
        margin-bottom: 1.5rem;
    }

    .providers-grid {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(380px, 1fr));
        gap: 1.5rem;
    }

    .provider-card {
        background: white;
        border-radius: 12px;
        padding: 1.5rem;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
        transition: transform 0.2s, box-shadow 0.2s;
        border: 2px solid transparent;
    }

    .provider-card.active {
        border-color: #4caf50;
    }

    .provider-card.inactive {
        opacity: 0.7;
    }

    .provider-card:hover {
        transform: translateY(-4px);
        box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
    }

    .provider-header {
        display: flex;
        align-items: flex-start;
        gap: 1rem;
        margin-bottom: 1rem;
    }

    .provider-icon {
        font-size: 2.5rem;
        line-height: 1;
    }

    .provider-info {
        flex: 1;
    }

    .provider-info h3 {
        margin: 0 0 0.25rem 0;
        font-size: 1.125rem;
        color: #333;
    }

    .provider-type {
        display: inline-block;
        padding: 0.25rem 0.625rem;
        background: #e3f2fd;
        color: #1976d2;
        border-radius: 12px;
        font-size: 0.75rem;
        font-weight: 500;
        text-transform: capitalize;
    }

    .provider-status {
        flex-shrink: 0;
    }

    .status-toggle {
        padding: 0.5rem 1rem;
        background: #f5f5f5;
        color: #666;
        border: 1px solid #e0e0e0;
        border-radius: 20px;
        cursor: pointer;
        font-size: 0.875rem;
        font-weight: 500;
        transition: all 0.2s;
    }

    .status-toggle.active {
        background: #e8f5e9;
        color: #4caf50;
        border-color: #4caf50;
    }

    .status-toggle:hover {
        opacity: 0.8;
    }

    .provider-details {
        margin: 1rem 0;
        padding: 1rem;
        background: #f9f9f9;
        border-radius: 8px;
    }

    .detail-item {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 0.5rem;
    }

    .detail-item:last-child {
        margin-bottom: 0;
    }

    .detail-item .label {
        font-weight: 500;
        color: #666;
        font-size: 0.875rem;
    }

    .detail-item .value {
        color: #333;
        font-size: 0.875rem;
    }

    .detail-item .value.endpoint {
        font-family: monospace;
        font-size: 0.8rem;
    }

    .price-info {
        display: flex;
        gap: 0.5rem;
    }

    .price-item {
        padding: 0.125rem 0.375rem;
        background: #e8f5e9;
        color: #2e7d32;
        border-radius: 4px;
        font-size: 0.75rem;
        font-weight: 500;
    }

    .free-badge {
        padding: 0.25rem 0.5rem;
        background: #e3f2fd;
        color: #1565c0;
        border-radius: 4px;
        font-size: 0.75rem;
        font-weight: 500;
    }

    .provider-actions {
        display: flex;
        gap: 0.5rem;
        margin-top: 1rem;
    }

    .btn-edit,
    .btn-delete {
        flex: 1;
        padding: 0.625rem;
        border: none;
        border-radius: 6px;
        cursor: pointer;
        font-size: 0.875rem;
        font-weight: 500;
        transition: opacity 0.2s;
    }

    .btn-edit {
        background: #e3f2fd;
        color: #1976d2;
    }

    .btn-delete {
        background: #ffebee;
        color: #d32f2f;
    }

    .btn-edit:hover,
    .btn-delete:hover {
        opacity: 0.8;
    }

    /* Modal styles */
    .modal-overlay {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(0, 0, 0, 0.5);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 1000;
    }

    .modal {
        background: white;
        border-radius: 12px;
        width: 90%;
        max-width: 600px;
        max-height: 90vh;
        overflow-y: auto;
    }

    .modal-header {
        padding: 1.5rem;
        border-bottom: 1px solid #e0e0e0;
        display: flex;
        justify-content: space-between;
        align-items: center;
    }

    .modal-header h3 {
        margin: 0;
        font-size: 1.25rem;
        color: #333;
    }

    .close-button {
        background: none;
        border: none;
        font-size: 2rem;
        color: #999;
        cursor: pointer;
        line-height: 1;
        padding: 0;
        width: 32px;
        height: 32px;
    }

    .modal-form {
        padding: 1.5rem;
    }

    .form-group {
        margin-bottom: 1.25rem;
    }

    .form-group.checkbox {
        display: flex;
        align-items: center;
    }

    .form-group.checkbox label {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        margin: 0;
        cursor: pointer;
    }

    .form-group label {
        display: block;
        margin-bottom: 0.5rem;
        color: #333;
        font-weight: 500;
        font-size: 0.9rem;
    }

    .form-group input,
    .form-group select {
        width: 100%;
        padding: 0.75rem;
        border: 1px solid #e0e0e0;
        border-radius: 6px;
        font-size: 0.9375rem;
        font-family: inherit;
    }

    .form-group input:focus,
    .form-group select:focus {
        outline: none;
        border-color: #128C7E;
    }

    .form-group small {
        display: block;
        margin-top: 0.375rem;
        color: #666;
        font-size: 0.8rem;
    }

    .form-group small a {
        color: #128C7E;
        text-decoration: none;
    }

    .form-group small a:hover {
        text-decoration: underline;
    }

    .provider-desc {
        color: #1976d2 !important;
        font-style: italic;
    }

    .model-details {
        margin-top: 0.75rem;
        padding: 0.75rem;
        background: #f5f7fb;
        border-radius: 8px;
        border: 1px solid #e0e0e0;
    }

    .model-pricing {
        margin-bottom: 0.5rem;
    }

    .price-row {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 0.25rem 0;
    }

    .price-label {
        color: #666;
        font-size: 0.85rem;
    }

    .price-value {
        color: #2e7d32;
        font-weight: 500;
        font-size: 0.85rem;
    }

    .context-info {
        color: #666;
        font-size: 0.85rem;
        padding-top: 0.5rem;
        border-top: 1px solid #e0e0e0;
    }

    .modal-actions {
        display: flex;
        gap: 1rem;
        margin-top: 1.5rem;
    }

    .btn-cancel,
    .btn-submit {
        flex: 1;
        padding: 0.75rem;
        border: none;
        border-radius: 6px;
        cursor: pointer;
        font-size: 0.9375rem;
        font-weight: 600;
    }

    .btn-cancel {
        background: #f5f5f5;
        color: #666;
    }

    .btn-submit {
        background: linear-gradient(135deg, #128C7E 0%, #075E54 100%);
        color: white;
    }

    .btn-cancel:hover,
    .btn-submit:hover {
        opacity: 0.9;
    }
</style>
